import type { FC } from "react";
import { useMemo, useState, useLayoutEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Options } from "highcharts";

import { LEGEND_POSITIONS, LineGraph } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import type { ISwitcherConfigItem } from "app/components/React/WidgetContainer/types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { useBrandVisibilityGraphData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/useBrandVisibilityGraphData";
import {
    capVisibleBrands,
    recolorPreservingCoreAverage,
    updateBrandsVisibility,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/coreCompetitorsUtils";
import { useCoreCompetitorsToggle } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/useCoreCompetitorsToggle";
import { useGranularitySwitcher } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useGranularitySwitcher";
import { LLMAnnotationsContextProvider } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMAnnotationContextProvider";
import {
    getBaseWidgetRequestParams,
    getWidgetNoDataProps,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { getLineGraphConfig } from "./config";
import { MAX_SELECTED_BRANDS, MIN_DAYS_FOR_WEEKLY_GRANULARITY } from "./consts";
import type { SeriesLineData, SeriesLineItem } from "./types";
import { transformData, calculateDefaultGranularity } from "./utils";

export const BrandsVisibilityComponent: FC<ILLMReportWidget> = ({
    campaign,
    campaignConfig,
    reportStatus,
    reportDuration,
}) => {
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const apiParams = useMemo(
        () => getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
        [campaign, campaignConfig, reportDuration],
    );

    const {
        data,
        isLoading: isLoadingData,
        isError,
        error,
    } = useBrandVisibilityGraphData(apiParams, { enabled: canLoadData });

    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const [transformedData, setTransformedData] = useState<SeriesLineData>();
    const hasInitializedGranularity = useRef<boolean>(false);
    const reportDays = reportDuration?.to.endOf("day").diff(reportDuration?.from, "days") ?? 0;

    const hasCompetitiveSet = campaign?.competitors?.length > 0;

    const { isCoreCompetitorsOn, onOffSwitchConfig } = useCoreCompetitorsToggle({
        hasCompetitiveSet,
    });
    const granularitySwitcherItems: ISwitcherConfigItem[] = [
        {
            title: "D",
            value: GranularityNames.DAILY,
            disabled: false,
        },
        {
            title: "W",
            value: GranularityNames.WEEKLY,
            disabled: reportDays < MIN_DAYS_FOR_WEEKLY_GRANULARITY,
        },
    ];

    const { selectedGranularity, setSelectedGranularity, granularitySwitcherConfig } = useGranularitySwitcher({
        items: granularitySwitcherItems,
        defaultValue: GranularityNames.DAILY,
        trackingName: "Brands Visibility Graph",
    });

    const topicFilteredData = useMemo(() => {
        if (!transformedData || !queryTopic) {
            return [];
        }
        const lowercaseTopicName = (queryTopic as string).toLowerCase();
        return Object.entries(transformedData).find(([key]) => key.toLowerCase() === lowercaseTopicName)?.[1] ?? [];
    }, [transformedData, queryTopic]);

    // Determine error vs no-data states
    const hasData = !!topicFilteredData?.length;
    const shouldShowNoData = !hasData;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    const graphConfig = useMemo(() => {
        const lineGraphData = topicFilteredData?.length > 0 ? topicFilteredData : [];
        return getLineGraphConfig(lineGraphData, selectedGranularity, reportDuration) as Options;
    }, [topicFilteredData, selectedGranularity, reportDuration]);

    const showMarkers = useMemo<boolean>(
        () => !!topicFilteredData?.some(({ visible, data: topicData }) => visible && topicData.length === 1),
        [topicFilteredData],
    );

    const coreAverageIndex = topicFilteredData.findIndex((item) => item.isCoreAverage);
    const separatorAboveIndex = coreAverageIndex >= 0 ? coreAverageIndex : null;

    useLayoutEffect(() => {
        setTransformedData(undefined);
        setSelectedGranularity(GranularityNames.DAILY);
        hasInitializedGranularity.current = false;
    }, [campaign?.id]);

    useLayoutEffect(() => {
        if (data?.graph) {
            const recommendedGranularity = calculateDefaultGranularity(data?.graph, reportDays);
            if (!hasInitializedGranularity.current && recommendedGranularity) {
                setSelectedGranularity(recommendedGranularity);
                hasInitializedGranularity.current = true;
            }

            try {
                const result = transformData(campaign, data.graph, selectedGranularity, isCoreCompetitorsOn);
                setTransformedData(result);
            } catch (transformDataError: unknown) {
                const errorMessage =
                    transformDataError instanceof Error
                        ? transformDataError.message
                        : typeof transformDataError === "string"
                          ? transformDataError
                          : JSON.stringify(transformDataError);
                Sentry.errorTracker({
                    e: transformDataError,
                    message: "Error transforming brands visibility data",
                    level: "error",
                    params: {
                        extra: {
                            moduleName: "llm-tracker",
                            campaignId: campaign?.id,
                            dataLength: data?.graph?.length,
                            errorMessage,
                        },
                    },
                });

                setTransformedData(undefined);
            }
        } else {
            setTransformedData(undefined);
            hasInitializedGranularity.current = false;
        }
    }, [data, selectedGranularity, campaign, error, isCoreCompetitorsOn]);

    const onLegendSelection = (legendItems: SeriesLineItem[]): void => {
        const selectedIds = new Set(legendItems.map((item) => item.id));
        const trackableTopicBrands = topicFilteredData.filter((b) => !b.isCoreAverage);
        const prevIds = new Set(trackableTopicBrands.filter((b) => b.visible).map((b) => b.id));
        const enabledBrandId = [...selectedIds].find((id) => !prevIds.has(id));
        const disabledBrandId = [...prevIds].find((id) => !selectedIds.has(id));

        if (enabledBrandId) {
            const { name, total } = legendItems.find((item) => item.id === enabledBrandId);
            allTrackers.dispatchTrackEvent("Graph Filter", "Brands", `${name}/${(total * 100).toFixed(0)}%/add`);
        } else if (disabledBrandId) {
            const { name, total } = topicFilteredData.find((item) => item.id === disabledBrandId);
            allTrackers.dispatchTrackEvent("Graph Filter", "Brands", `${name}/${(total * 100).toFixed(0)}%/remove`);
        }

        setTransformedData((prev) => {
            if (!prev) {
                return prev;
            }

            const changedBrands = new Map(
                trackableTopicBrands
                    .filter((item) => prevIds.has(item.id) !== selectedIds.has(item.id))
                    .map((item) => [item.name, selectedIds.has(item.id)]),
            );

            const lowercaseQueryTopic = (queryTopic as string)?.toLowerCase();

            return Object.fromEntries(
                Object.entries(prev).map(([topicName, brands]) => {
                    const isCurrentTopic = topicName.toLowerCase() === lowercaseQueryTopic;
                    const updatedBrands = updateBrandsVisibility(brands, {
                        isCurrentTopic,
                        selectedIds,
                        changedBrands,
                    });
                    const limitedBrands = capVisibleBrands(updatedBrands, MAX_SELECTED_BRANDS);

                    return [topicName, recolorPreservingCoreAverage(limitedBrands)];
                }),
            );
        });
    };

    const legendValueFilter = (value: number): string => percentageSignFilter()(value, 0) as string;

    const excelURL =
        canLoadData && campaign?.id
            ? `/api/aiTracker/reports/brandVisibilityReport/Graph/Excel?${requestParams(apiParams)}`
            : "";

    const onExcelClick = (): void => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");
    };

    return (
        <WidgetContainer
            {...getWidgetNoDataProps(shouldShowNoData, isError)}
            title={translate("llm.brands-visibility.title")}
            tooltip={translate("llm.brands-visibility.tooltip")}
            isLoading={shouldShowLoading}
            loaderWidth={"100%"}
            loaderHeight={300}
            withBorder
            excelUrl={excelURL}
            onExcelClick={onExcelClick}
            dynamicSwitcherConfig={granularitySwitcherConfig}
            onOffSwitchConfig={onOffSwitchConfig}
            headerHeight={80}
        >
            <LLMAnnotationsContextProvider
                key={`brands-visibility-graph-${campaign?.id}`}
                chartName="brands-visibility-graph"
                granularity={selectedGranularity}
                campaign={campaign}
            >
                <LineGraph<SeriesLineItem>
                    data={topicFilteredData}
                    graphConfig={graphConfig}
                    shouldPresentLegend
                    shouldPresentValueAtLegend
                    isSelectableCheckboxes
                    isLegendSearch
                    legendSearchPlaceholder={translate("llm.brands-visibility.graph.search.placeholder")}
                    disabledLegendItemTooltip={translate("checkbox.max.selection")}
                    withMarkers={showMarkers}
                    legendsMaxWidth={285}
                    legendsHeight="200px"
                    legendHeaders={[
                        translate("llm.brands-visibility.graph.legend.header.brands"),
                        translate("llm.brands-visibility.graph.legend.header.visibility"),
                    ]}
                    isToggleAllEnabled
                    legendPosition={LEGEND_POSITIONS.RIGHT}
                    legendValueFilter={legendValueFilter}
                    legendToggleHandlerCallback={onLegendSelection}
                    maxLegendItemsSelected={MAX_SELECTED_BRANDS}
                    keepSameLegendsOnDataUpdate={false}
                    presentLineSeparatorAboveItemIndex={separatorAboveIndex}
                />
            </LLMAnnotationsContextProvider>
        </WidgetContainer>
    );
};
